import { useState, useEffect } from 'react';
import { Pencil } from 'lucide-react';
import { UserAvatar } from '@/components/profile/UserAvatar';
import { AvatarUploadDialog } from '@/components/profile/AvatarUploadDialog';
import { cn } from '@/lib/utils';

interface EditableAvatarProps {
  avatarUrl: string | null | undefined;
  username: string | null | undefined;
  email?: string | null;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  fallbackClassName?: string;
  editable?: boolean;
  onAvatarUpdated?: (newUrl: string) => void;
}

export function EditableAvatar({
  avatarUrl,
  username,
  email,
  size = 'md',
  className,
  fallbackClassName,
  editable = false,
  onAvatarUpdated
}: EditableAvatarProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentAvatarUrl, setCurrentAvatarUrl] = useState(avatarUrl);

  // Sync with prop changes using useEffect to prevent render-phase updates
  useEffect(() => {
    if (!dialogOpen) {
      setCurrentAvatarUrl(avatarUrl);
    }
  }, [avatarUrl, dialogOpen]);

  const handleClick = (e: React.MouseEvent) => {
    if (editable) {
      e.stopPropagation();
      e.preventDefault();
      setDialogOpen(true);
    }
  };

  const handleSuccess = (newUrl: string) => {
    setCurrentAvatarUrl(newUrl);
    onAvatarUpdated?.(newUrl);
  };

  if (!editable) {
    return (
      <UserAvatar
        avatarUrl={currentAvatarUrl}
        username={username}
        email={email}
        size={size}
        className={className}
        fallbackClassName={fallbackClassName}
      />
    );
  }

  return (
    <>
      <div
        className="group relative cursor-pointer inline-block"
        onClick={handleClick}
        role="button"
        tabIndex={0}
        aria-label="Change profile picture"
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setDialogOpen(true);
          }
        }}
      >
        <UserAvatar
          avatarUrl={currentAvatarUrl}
          username={username}
          email={email}
          size={size}
          className={className}
          fallbackClassName={fallbackClassName}
        />
        {/* Hover overlay */}
        <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          <Pencil className="h-5 w-5 text-white" />
        </div>
      </div>

      <AvatarUploadDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        currentAvatarUrl={currentAvatarUrl ?? null}
        username={username ?? null}
        email={email}
        onSuccess={handleSuccess}
      />
    </>
  );
}
